import { payInParts, type SplitPart } from '../cash/split';
import type { PaymentMethod } from '../generated/enums';
import { apiFetch } from './client';
import { sellableGarmentPath } from './queryPaths';
import type { SaleInvoice, SaveSaleInput, SellableGarment } from './saleInvoices';

/**
 * The retail sale's writes both clients send — the website's Sell page and the phone's — built once,
 * on `bookingCommands.ts`'s terms. Every figure in the answer is the server's.
 */

/** One garment by its tag, with what stops it being sold. 404 when the branch has no such tag. */
export function findSellableGarment(serial: string) {
  return apiFetch<SellableGarment>(sellableGarmentPath(serial));
}

export function createSale(input: SaveSaleInput) {
  return apiFetch<SaleInvoice>('/api/sale-invoice', { method: 'POST', body: input });
}

/** Money taken later against what a sale left owed. */
export function takeSalePayment(
  id: number,
  input: {
    method: PaymentMethod;
    amount: number;
    reference: string | null;
    /** The account it went into (split payments, step 3); absent or null is the default. */
    cashAccountId?: number | null;
  },
) {
  return apiFetch<SaleInvoice>(`/api/sale-invoice/${id}/payment`, { method: 'POST', body: input });
}

/**
 * Money taken later, part by part (canvas "Money in, by account"): a payment per account box, each
 * naming its account, through `payInParts` — nothing saved and refused throws; a refusal part-way
 * answers what was saved.
 */
export function takeSalePaymentInParts(id: number, parts: readonly SplitPart[]) {
  return payInParts(parts, (part, amount) =>
    takeSalePayment(id, {
      method: part.method,
      amount,
      reference: part.reference.trim() || null,
      cashAccountId: part.accountId,
    }),
  );
}

/** With a reason; a tax invoice only while unpaid, a bill of supply even when paid (refunded). */
export function cancelSale(id: number, reason: string) {
  return apiFetch<SaleInvoice>(`/api/sale-invoice/${id}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}
